import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock } from "lucide-react";

interface CTASectionProps {
  preloaderComplete?: boolean;
}

const CTASection = ({ preloaderComplete = true }: CTASectionProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const shouldAnimate = isInView && preloaderComplete;

  return (
    <section ref={ref} className="relative py-16 md:py-32 px-4 md:px-6 overflow-hidden">
      {/* Moderate gradient background */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.10) 0%, transparent 60%)' }} />
      <div className="container mx-auto max-w-4xl text-center relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-foreground mb-3 md:mb-4 headline-display px-2"
        >
          Ready to deploy <span className="ayra-accent">AI voice agents</span>?
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-lg md:text-xl lg:text-2xl text-muted-foreground mb-6 md:mb-8 body-text"
        >
          Whether you're a business owner or an agency, Ayra helps you capture leads 24/7.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-base md:text-lg text-foreground font-medium mb-8 md:mb-10 tracking-tight"
        >
          Book a call. See how it works. Start capturing leads.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <a href="https://calendly.com/sameer-ayra/30min" target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-foreground text-background px-8 md:px-10 py-6 md:py-7 text-base md:text-lg font-medium"
            >
              Book a Call
              <ArrowRight className="ml-2 h-4 md:h-5 w-4 md:w-5" />
            </Button>
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-6 md:mt-8 text-sm md:text-base text-muted-foreground"
        >
          AI voice agents and chat agents built for businesses and agencies.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-3 md:mt-4 flex items-center justify-center gap-2 text-xs md:text-sm text-muted-foreground"
        >
          <Clock className="h-3 md:h-4 w-3 md:w-4" />
          Applications reviewed within 48 hours.
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
